import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { task } from 'ember-concurrency';
import isValidEmail from 'cio/utils/validators/email';
import FormValidations from 'cio/utils/form-validations';

import LoginController from 'cio/pods/login/controller';
import SsoService from 'cio/services/sso';
import RouterService from '@ember/routing/router-service';
import LoginService from 'cio/services/login';

const Validations = {
  email(value: string) {
    if (!isValidEmail(value)) {
      return 'Please enter a valid email address';
    }

    return true;
  },
};

export default class IndexController extends Controller {
  queryParams = ['loggedOut', 'email', 'origin'];

  @service sso!: SsoService;
  @service router!: RouterService;
  @service login!: LoginService;
  @controller('login') loginController!: LoginController;

  loggedOut = null;
  email: null | string = null;
  origin: null | string = null;
  _emailInputTouched = false;

  _validations: FormValidations;

  constructor() {
    super(...arguments);

    this._validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'loginController',
    });
  }

  submitTask = task(async (formSubmitEvent?: Event) => {
    if (formSubmitEvent) {
      formSubmitEvent.preventDefault();
    }

    try {
      await this._validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    try {
      let { ssoConfiguration, userExternalIdentityEmail } = await this.sso.fetchSSOInfo({
        email: this.loginController.email,
      });

      if (ssoConfiguration?.identityProvider == null) {
        return this.loginController.loginTaskEmailPassword.perform();
      }

      // redirect to SSO login view if SSO configuration was returned
      this.loginController.setProperties({
        ssoConfigurationForLogin: ssoConfiguration,
        userExternalIdentityEmail,
      });
      return this.router.transitionTo('login.sso');
    } catch (e) {
      // 404 error fetching SSO configuration, so SSO is not enabled: login using e-mail/password
      this.router.transitionTo('login.password');
    }

    return;
  });

  @action
  onEmailBlur() {
    this.set('_emailInputTouched', this._emailInputTouched || Boolean(this.loginController.email));
  }

  @action
  onEmailInput(value: string) {
    this.loginController.set('email', value);
    this._validations.validateAttribute('email');
  }

  @action
  setAutocompletedPassword(event: InputEvent) {
    this.loginController.set('password', (event.target as HTMLInputElement).value);
  }

  @action
  setAutocompletedTwoFactorAuthCode(event: InputEvent) {
    this.loginController.set('twoFactorAuthCode', (event.target as HTMLInputElement).value);
  }
}
